import './LeaveConfig.scss';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, DatePicker, Input, Typography } from 'antd';
import {
  DeleteOutlined,
  PlusOutlined,
  SaveOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import DeleteConfirmationModal from '../components/Modals/DeleteConfirmationModal';
import { ConfirmAction } from '../utils/types';
import { useUser } from '../context/UserContext';
import {
  fetchData,
  getErrorMessage,
  Notify,
  postData,
  updateData,
} from '../utils/constants';
import { apiClient } from '../utils/httpClient';
import { parseDateOnly, toUtcDateString } from '../utils/date';
import {
  getLeaveLegendColors,
  updateLeaveLegendColors,
} from '../utils/generalConfigurationService';

// The set of legend items is fixed in code (it mirrors RequestTypeEnum plus a
// few status/utility keys) - only each item's color is user-configurable.
const LEGEND_ITEMS: { key: string; label: string }[] = [
  { key: 'APPROVED', label: 'Approved leave' },
  { key: 'PENDING', label: 'Pending leave' },
  { key: 'BANK_HOLIDAY', label: 'Bank holiday' },
  { key: 'ANNUAL_LEAVE', label: 'Annual Leave' },
  { key: 'MARRIAGE', label: 'Marriage' },
  { key: 'CHILD_BIRTH', label: 'Child Birth' },
  { key: 'DEATH_OF_RELATIVE', label: 'Death of Relative' },
  { key: 'BLOOD_DONATION', label: 'Blood Donation' },
  { key: 'UNPAID_LEAVE', label: 'Unpaid Leave' },
];

type BankHoliday = {
  id: number;
  description: string;
  date: Date;
};

const LeaveConfig = () => {
  const { authUser } = useUser();
  const [legendColors, setLegendColors] = useState<Record<string, string>>({});
  const [initialLegendColors, setInitialLegendColors] = useState<
    Record<string, string>
  >({});
  const [bankHolidays, setBankHolidays] = useState<BankHoliday[]>([]);
  const [initialHolidays, setInitialHolidays] = useState<BankHoliday[]>([]);

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [onConfirmDelete, setOnConfirmDelete] = useState<ConfirmAction | null>(
    null,
  );

  const fetchLegendColors = useCallback(async () => {
    try {
      const config = await getLeaveLegendColors();
      setLegendColors(config.colors || {});
      setInitialLegendColors(config.colors || {});
    } catch (error) {
      console.error('Failed to fetch leave legend colors:', error);
      Notify('error', 'Could not load leave type colors. Please try again.');
    }
  }, []);

  const fetchHolidays = useCallback(async () => {
    try {
      await fetchData(
        `${process.env.REACT_APP_API_URL}/holidays/getAllHolidays`,
        (data: BankHoliday[]) => {
          const holidays = data.map((holiday: BankHoliday) => ({
            ...holiday,
            date: parseDateOnly(holiday.date),
          }));
          setInitialHolidays(
            holidays.map((holiday: BankHoliday) => ({
              ...holiday,
              date: new Date(holiday.date),
            })),
          );
          setBankHolidays(holidays);
        },
      );
    } catch (error) {
      console.error('Failed to fetch holidays data:', error);
      Notify('error', 'Could not load holiday data. Please try again.');
    }
  }, []);

  useEffect(() => {
    if (!authUser) return;
    fetchHolidays();
    fetchLegendColors();
  }, [authUser, fetchHolidays, fetchLegendColors]);

  const hasHolidayChanges = useMemo(() => {
    const initialHolidaysById = new Map(
      initialHolidays.map((holiday) => [holiday.id, holiday]),
    );

    return bankHolidays.some((holiday) => {
      const initialHoliday = initialHolidaysById.get(holiday.id);

      return (
        !initialHoliday ||
        initialHoliday.description !== holiday.description ||
        toUtcDateString(initialHoliday.date) !== toUtcDateString(holiday.date)
      );
    });
  }, [bankHolidays, initialHolidays]);

  const hasLegendColorChanges = useMemo(
    () =>
      LEGEND_ITEMS.some(
        (item) => legendColors[item.key] !== initialLegendColors[item.key],
      ),
    [legendColors, initialLegendColors],
  );

  const saveConfigs = async () => {
    const initialHolidaysById = new Map(
      initialHolidays.map((holiday) => [holiday.id, holiday]),
    );
    const holidayDates = bankHolidays.map((holiday) =>
      toUtcDateString(holiday.date),
    );

    if (new Set(holidayDates).size !== holidayDates.length) {
      Notify('error', 'Only one bank holiday can be configured for each date.');
      return;
    }

    const newHolidays = bankHolidays.filter(
      (holiday) => !initialHolidaysById.has(holiday.id),
    );
    const updatedHolidays = bankHolidays.filter((holiday) => {
      const initialHoliday = initialHolidaysById.get(holiday.id);

      return (
        initialHoliday &&
        (initialHoliday.description !== holiday.description ||
          toUtcDateString(initialHoliday.date) !==
            toUtcDateString(holiday.date))
      );
    });

    const holidayChangesPresent =
      newHolidays.length > 0 || updatedHolidays.length > 0;

    if (!holidayChangesPresent && !hasLegendColorChanges) {
      Notify('info', 'No changes to save.');
      return;
    }

    try {
      if (holidayChangesPresent) {
        await Promise.all(
          newHolidays.map((holiday) => {
            const holidayToSave = {
              description: holiday.description,
              date: toUtcDateString(holiday.date),
            };
            return postData(
              `${process.env.REACT_APP_API_URL}/holidays/addNewLegalHoliday`,
              holidayToSave,
            );
          }),
        );

        await Promise.all(
          updatedHolidays.map((holiday) =>
            updateData(
              `${process.env.REACT_APP_API_URL}/holidays/updateLegalHoliday/${holiday.id}`,
              {
                description: holiday.description,
                date: toUtcDateString(holiday.date),
              },
            ),
          ),
        );

        await fetchHolidays();
      }

      if (hasLegendColorChanges) {
        const updatedColors = await updateLeaveLegendColors(legendColors);
        setLegendColors(updatedColors.colors || {});
        setInitialLegendColors(updatedColors.colors || {});
      }

      Notify('success', 'Configurations saved successfully!');
    } catch (err) {
      console.error('Error saving configurations:', err);
      Notify(
        'error',
        getErrorMessage(err, 'Failed to save one or more configurations.'),
      );
    }
  };

  // leave types
  const handleLegendColorUpdate = useCallback((key: string, value: string) => {
    setLegendColors((prev) => ({ ...prev, [key]: value }));
  }, []);

  const leaveTypesConfig = (
    <div className="section-wrapper">
      <Typography.Title level={4}>Leave types</Typography.Title>
      <Typography.Paragraph type="secondary">
        Pick the color used for each leave type/status in the Team Calendar.
      </Typography.Paragraph>
      {LEGEND_ITEMS.map((item) => (
        <div key={item.key} className="leave-type-row">
          <Input value={item.label} disabled />
          <input
            type="color"
            value={legendColors[item.key] || '#000000'}
            onChange={(e) => handleLegendColorUpdate(item.key, e.target.value)}
            className="leave-type-color-picker"
          />
        </div>
      ))}
    </div>
  );

  // annual holidays
  const handleHolidayUpdate = useCallback(
    (id: number, field: keyof BankHoliday, value: string | Date | boolean) => {
      setBankHolidays((prev) =>
        prev.map((holiday) =>
          holiday.id === id ? { ...holiday, [field]: value } : holiday,
        ),
      );
    },
    [],
  );

  const addBankHoliday = useCallback(() => {
    const newHoliday: BankHoliday = {
      id: Date.now(),
      description: '',
      date: new Date(),
    };
    setBankHolidays((prev) => [...prev, newHoliday]);
  }, []);

  const deleteBankHoliday = useCallback(
    async (id: number) => {
      try {
        await apiClient.delete(
          `${process.env.REACT_APP_API_URL}/holidays/deleteFreeDayById/${id}`,
        );
        await fetchHolidays();
      } catch (error) {
        console.error('Error deleting holidays:', error);
        Notify('error', 'Failed to delete holiday.');
      }
    },
    [fetchHolidays],
  );

  const handleDeleteBankHoliday = useCallback(
    (id: number) => {
      setOnConfirmDelete(() => () => deleteBankHoliday(id));
      setDeleteModalOpen(true);
    },
    [deleteBankHoliday],
  );

  const sortedBankHolidays = useMemo(
    () =>
      [...bankHolidays].sort((a, b) => {
        const yearDiff = b.date.getFullYear() - a.date.getFullYear();
        return yearDiff !== 0 ? yearDiff : a.date.getTime() - b.date.getTime();
      }),
    [bankHolidays],
  );

  const bankHolidaysConfig = (
    <div className="section-wrapper">
      <Typography.Title level={4}>Bank Holidays</Typography.Title>
      {sortedBankHolidays.length === 0 ? (
        <p>
          <em>No existing bank holidays</em>
        </p>
      ) : (
        sortedBankHolidays.map((holiday) => (
          <div key={holiday.id} className="bank-holiday-row">
            <Input
              placeholder="Bank holiday name"
              value={holiday.description}
              onChange={(e) =>
                handleHolidayUpdate(holiday.id, 'description', e.target.value)
              }
            />
            <DatePicker
              value={holiday.date ? dayjs(holiday.date) : null}
              onChange={(date) =>
                date && handleHolidayUpdate(holiday.id, 'date', date.toDate())
              }
              format="YYYY-MM-DD"
            />
            <Button
              danger
              icon={<DeleteOutlined />}
              onClick={() => handleDeleteBankHoliday(holiday.id)}
              title="Delete holiday"
            />
          </div>
        ))
      )}
      <Button onClick={addBankHoliday} type="primary" icon={<PlusOutlined />}>
        Add Bank Holiday
      </Button>
    </div>
  );

  return (
    <div>
      <Typography.Title level={2}>Configurations</Typography.Title>

      {leaveTypesConfig}
      {bankHolidaysConfig}

      <Button
        type="primary"
        onClick={saveConfigs}
        title="Save configurations"
        disabled={!hasHolidayChanges && !hasLegendColorChanges}
        icon={<SaveOutlined />}
      >
        Save configurations
      </Button>

      {onConfirmDelete && (
        <DeleteConfirmationModal
          isOpen={deleteModalOpen}
          setIsOpen={setDeleteModalOpen}
          onCancel={() => setDeleteModalOpen(false)}
          onConfirm={() => {
            onConfirmDelete();
            setDeleteModalOpen(false);
          }}
        />
      )}
    </div>
  );
};
export default LeaveConfig;
